import { graphqlClient, useFormDataHeader } from 'src/graphql/client'
import {
  AddFreeCampaignDocument,
  AddFreeCampaignMutationVariables,
  ChangeSubscriptionDocument,
  ChangeSubscriptionMutationVariables,
  CreateAccountDocument,
  CreateAccountMutationVariables,
  CreateAffiliateDocument,
  CreateAffiliateMutationVariables,
  CreateCampaignDocument,
  CreateCampaignMutationVariables,
  CreateFreeAffiliateDocument,
  CreateFreeAffiliateMutationVariables,
  CreateOrUpdateBackPanelDocument,
  CreateOrUpdateBackPanelMutationVariables,
  CreateSparseUserDocument,
  CreateSparseUserMutationVariables,
  CreateUserLoginLinkDocument,
  CreateUserLoginLinkMutationVariables,
  LoginUserDocument,
  LoginUserMutationVariables,
  RevertBulkOrderDocument,
  RevertBulkOrderMutationVariables,
  SendMagicLinkDocument,
  SendMagicLinkMutationVariables,
  UpdateCardDocument,
  UpdateCardMutationVariables,
  UpdateUserLabelOverridesDocument,
  UpdateUserLabelOverridesMutationVariables,
  UpgradeToAffiliateDocument,
  UpgradeToAffiliateMutationVariables,
  UpgradeToGreenerStillAffiliateDocument,
  UpgradeToGreenerStillAffiliateMutationVariables,
  UpgradeToReferringCustomerDocument,
  UpgradeToReferringCustomerMutationVariables,
} from 'src/graphql/generated/graphql'
import * as graphql from 'src/graphql/generated/graphql'
import { sanitizeEmail } from 'src/helpers/sanitizeEmail'

const sanitizeAccountInput = (accountInput: graphql.AccountInput) => {
  return accountInput.email
    ? {
        ...accountInput,
        email: sanitizeEmail(accountInput.email),
      }
    : accountInput
}

export const acceptCampaignShare = async (
  variables: graphql.AcceptCampaignShareMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.AcceptCampaignShareDocument,
    variables,
  )
  return res
}

export const acceptMembershipInvite = async (
  variables: graphql.AcceptMembershipInviteMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.AcceptMembershipInviteDocument,
    variables,
  )
  return res
}

export const acceptSharedContact = async (
  variables: graphql.AcceptSharedContactMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.AcceptSharedContactDocument,
    variables,
  )
  return res
}

export const bulkDeleteSendableCards = async (
  variables: graphql.BulkDeleteSendableCardsMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.BulkDeleteSendableCardsDocument,
    variables,
  )
  return res
}

export const declineMembershipInvite = async (
  variables: graphql.DeclineMembershipInviteMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.DeclineMembershipInviteDocument,
    variables,
  )
  return res
}

export const deleteCampaignShare = async (
  variables: graphql.DeleteCampaignShareMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.DeleteCampaignShareDocument,
    variables,
  )
  return res
}

export const deleteContactShare = async (
  variables: graphql.DeleteContactShareMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.DeleteContactShareDocument,
    variables,
  )
  return res
}

export const deleteReminder = async (
  variables: graphql.DeleteReminderMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.DeleteReminderDocument,
    variables,
  )
  return res
}

export const denyNotification = async (
  variables: graphql.DenyNotificationMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.DenyNotificationDocument,
    variables,
  )
  return res
}

export const cancelStripeSubscription = async (
  variables: graphql.CancelStripeSubscriptionMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.CancelStripeSubscriptionDocument,
      variables,
    )
  ).cancelStripeSubscription

export const createStripeOrder = async (
  variables: graphql.CreateStripeOrderMutationVariables,
) =>
  (await graphqlClient.request(graphql.CreateStripeOrderDocument, variables))
    .createStripeOrder

export const createAccount = async (
  variables: CreateAccountMutationVariables,
) => {
  const res = await graphqlClient.request(CreateAccountDocument, {
    ...variables,
    account: sanitizeAccountInput(variables.account),
  })

  return res
}

export const createAffiliate = async (
  variables: CreateAffiliateMutationVariables,
) => {
  const res = await graphqlClient.request(CreateAffiliateDocument, variables)

  return res
}

export const upgradeToReferringCustomer = async (
  variables: UpgradeToReferringCustomerMutationVariables,
) => {
  const res = await graphqlClient.request(
    UpgradeToReferringCustomerDocument,
    variables,
  )

  return res
}

export const createFreeAffiliate = async (
  variables: CreateFreeAffiliateMutationVariables,
) => {
  return await graphqlClient.request(CreateFreeAffiliateDocument, variables)
}

export const createUserLoginLink = async (
  variables: CreateUserLoginLinkMutationVariables,
) => await graphqlClient.request(CreateUserLoginLinkDocument, variables)

export const favoriteSendableCard = async (
  variables: graphql.FavoriteSendableCardMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.FavoriteSendableCardDocument,
    variables,
  )
  return res
}

export const getOrCreateEditableCardForRecipient = async (
  variables: graphql.GetOrCreateEditableCardForRecipientMutationVariables,
) =>
  await graphqlClient.request(
    graphql.GetOrCreateEditableCardForRecipientDocument,
    variables,
  )

export const updateRecipient = async (
  variables: graphql.UpdateRecipientMutationVariables,
) => await graphqlClient.request(graphql.UpdateRecipientDocument, variables)

export const sendPausedRecipients = async (
  variables: graphql.SendPausedRecipientsMutationVariables,
) =>
  await graphqlClient.request(graphql.SendPausedRecipientsDocument, variables)

export const validatePhoneNumber = async (
  variables: graphql.ValidatePhoneNumberMutationVariables,
) =>
  (await graphqlClient.request(graphql.ValidatePhoneNumberDocument, variables))
    .validatePhoneNumber

export const requestPhoneNumberVerification = async (
  variables: graphql.RequestPhoneNumberVerificationMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.RequestPhoneNumberVerificationDocument,
      variables,
    )
  ).requestPhoneNumberVerification

export const verifyPhoneNumber = async (
  variables: graphql.VerifyPhoneNumberMutationVariables,
) => graphqlClient.request(graphql.VerifyPhoneNumberDocument, variables)

export const phoneNumberVerifyForResource = async (
  variables: graphql.PhoneNumberVerifyForResourceMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.PhoneNumberVerifyForResourceDocument,
      variables,
    )
  ).phoneNumberVerifyForResource

export const validateEmail = async (
  variables: graphql.ValidateEmailMutationVariables,
) =>
  (await graphqlClient.request(graphql.ValidateEmailDocument, variables))
    .validateEmail

export const requestEmailVerificationCode = async (
  variables: graphql.RequestEmailVerificationCodeMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.RequestEmailVerificationCodeDocument,
      variables,
    )
  ).requestEmailVerificationCode

export const validateEmailVerificationCode = async (
  variables: graphql.ValidateEmailVerificationCodeMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.ValidateEmailVerificationCodeDocument,
      variables,
    )
  ).validateEmailVerificationCode

export const validateUsername = async (
  variables: graphql.ValidateUsernameMutationVariables,
) =>
  (await graphqlClient.request(graphql.ValidateUsernameDocument, variables))
    .validateUsername

export const sendMagicLink = async (
  variables: SendMagicLinkMutationVariables,
) => graphqlClient.request(SendMagicLinkDocument, variables)

export const loginUser = async (variables: LoginUserMutationVariables) =>
  graphqlClient.request(LoginUserDocument, variables)

export const createCard = async (
  variables: graphql.CreateCardMutationVariables,
) => await graphqlClient.request(graphql.CreateCardDocument, variables)

export const bulkDeleteCards = async (
  variables: graphql.BulkDeleteCardsMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.BulkDeleteCardsDocument,
    variables,
  )
  return res
}

export const cancelPendingCards = async (
  variables: graphql.CancelPendingCardsMutationVariables,
) =>
  (await graphqlClient.request(graphql.CancelPendingCardsDocument, variables))
    .cancelPendingCards

export const digitalCardCreate = async (
  variables: graphql.DigitalCardCreateMutationVariables,
) =>
  (await graphqlClient.request(graphql.DigitalCardCreateDocument, variables))
    .digitalCardCreate.digitalCard

export const digitalCardOrderCreate = async (
  variables: graphql.DigitalCardOrderCreateMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardOrderCreateDocument,
      variables,
    )
  ).digitalCardOrderCreate.order

export const digitalCardOrderPay = async (
  variables: graphql.DigitalCardOrderPayMutationVariables,
) =>
  (await graphqlClient.request(graphql.DigitalCardOrderPayDocument, variables))
    .digitalCardOrderPay.digitalCard

export const digitalCardRecipientFromNameUpsert = async (
  variables: graphql.DigitalCardRecipientFromNameUpsertMutationVariables,
) =>
  await graphqlClient.request(
    graphql.DigitalCardRecipientFromNameUpsertDocument,
    variables,
  )

export const digitalCardUpdate = async (
  variables: graphql.DigitalCardUpdateMutationVariables,
) =>
  (await graphqlClient.request(graphql.DigitalCardUpdateDocument, variables))
    .digitalCardUpdate.digitalCard

export const digitalCardOrderLineGiftCardAdd = async (
  variables: graphql.DigitalCardOrderLineGiftCardAddMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardOrderLineGiftCardAddDocument,
      variables,
    )
  ).digitalCardOrderLineGiftCardAdd.digitalCard

export const digitalCardOrderLineGiftCardRemove = async (
  variables: graphql.DigitalCardOrderLineGiftCardRemoveMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardOrderLineGiftCardRemoveDocument,
      variables,
    )
  ).digitalCardOrderLineGiftCardRemove.digitalCard

export const digitalCardUpdateShareStatus = async (
  variables: graphql.DigitalCardUpdateShareStatusMutationVariables,
) =>
  await graphqlClient.request(
    graphql.DigitalCardUpdateShareStatusDocument,
    variables,
  )

export const digitalCardDelete = async (
  variables: graphql.DigitalCardDeleteMutationVariables,
) => await graphqlClient.request(graphql.DigitalCardDeleteDocument, variables)

export const digitalCardRecipientCreate = async (
  variables: graphql.DigitalCardRecipientCreateMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardRecipientCreateDocument,
      variables,
    )
  ).digitalCardRecipientCreate.digitalCardRecipient

export const digitalCardRecipientLinkToAccount = async (
  variables: graphql.DigitalCardRecipientLinkToAccountMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardRecipientLinkToAccountDocument,
      variables,
    )
  ).digitalCardRecipientLinkToAccount.result

export const digitalGiftCardsRedeem = async (
  variables: graphql.DigitalGiftCardsRedeemMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalGiftCardsRedeemDocument,
      variables,
    )
  ).digitalGiftCardsRedeem

export const digitalCardAddPhoneNumberToAccount = async (
  variables: graphql.DigitalCardAddPhoneNumberToAccountMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.DigitalCardAddPhoneNumberToAccountDocument,
      variables,
    )
  ).digitalCardAddPhoneNumberToAccount

export const requestPhysicalCard = async (
  variables: graphql.RequestPhysicalCardMutationVariables,
) =>
  (await graphqlClient.request(graphql.RequestPhysicalCardDocument, variables))
    .requestPhysicalCard.requested

export const removeUserUpsell = async (
  variables: graphql.RemoveUserUpsellMutationVariables,
) => await graphqlClient.request(graphql.RemoveUserUpsellDocument, variables)

export const subscriptionAddonAdd = async (
  variables: graphql.SubscriptionAddonAddMutationVariables,
) =>
  await graphqlClient.request(graphql.SubscriptionAddonAddDocument, variables)

export const subscriptionAddonRemove = async (
  variables: graphql.SubscriptionAddonRemoveMutationVariables,
) =>
  await graphqlClient.request(
    graphql.SubscriptionAddonRemoveDocument,
    variables,
  )

export const createSparseUser = async (
  variables: CreateSparseUserMutationVariables,
) => graphqlClient.request(CreateSparseUserDocument, variables)

export const purchaseProduct = async (
  variables: graphql.PurchaseProductMutationVariables,
) => graphqlClient.request(graphql.PurchaseProductDocument, variables)

export const unfavoriteSendableCard = async (
  variables: graphql.UnfavoriteSendableCardMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.UnfavoriteSendableCardDocument,
    variables,
  )
  return res
}

export const upgradeToAffiliate = async (
  variables: UpgradeToAffiliateMutationVariables,
) => await graphqlClient.request(UpgradeToAffiliateDocument, variables)

export const upgradeToGreenerStillAffiliate = async (
  variables: UpgradeToGreenerStillAffiliateMutationVariables,
) =>
  await graphqlClient.request(UpgradeToGreenerStillAffiliateDocument, variables)

export const updateAccount = async (
  variables: graphql.UpdateAccountMutationVariables,
) =>
  await graphqlClient.request(graphql.UpdateAccountDocument, {
    ...variables,
    account: sanitizeAccountInput(variables.account),
  })

export const updateStorefrontMugshot = async (
  variables:
    | graphql.UpdateStorefrontMugshotMutationVariables
    | { image_file: File; tags: string[] },
) =>
  // @ts-ignore: we're sending "extra" variables as form data
  graphqlClient.request(graphql.UpdateStorefrontMugshotDocument, variables, {
    [useFormDataHeader]: 'true',
  })

export const updateSourceBillingAddress = async (
  variables: graphql.UpdateSourceBillingAddressMutationVariables,
) =>
  await (
    await graphqlClient.request(
      graphql.UpdateSourceBillingAddressDocument,
      variables,
    )
  ).updateSourceBillingAddress

export const updateCard = async (variables: UpdateCardMutationVariables) =>
  await graphqlClient.request(UpdateCardDocument, variables)

export const updateUserLabelOverrides = async (
  variables: UpdateUserLabelOverridesMutationVariables,
) => await graphqlClient.request(UpdateUserLabelOverridesDocument, variables)

export const updateCampaign = async (
  variables: graphql.UpdateCampaignMutationVariables,
) =>
  (await graphqlClient.request(graphql.UpdateCampaignDocument, variables))
    .updateCampaign

export const deleteCampaign = async (
  variables: graphql.DeleteCampaignMutationVariables,
) =>
  (await graphqlClient.request(graphql.DeleteCampaignDocument, variables))
    .deleteCampaign

export const shareCampaign = async (
  variables: graphql.ShareCampaignMutationVariables,
) =>
  (await graphqlClient.request(graphql.ShareCampaignDocument, variables))
    .shareCampaign

export const createOrder = async (
  variables: graphql.CreateOrderMutationVariables,
) => await graphqlClient.request(graphql.CreateOrderDocument, variables)

export const deleteOrder = async (
  variables: graphql.DeleteOrderMutationVariables,
) => await graphqlClient.request(graphql.DeleteOrderDocument, variables)

export const bulkDeleteOrders = async (
  variables: graphql.BulkDeleteOrdersMutationVariables,
) => await graphqlClient.request(graphql.BulkDeleteOrdersDocument, variables)

export const createOrderFromDigitalCard = async (
  variables: graphql.CreateOrderFromDigitalCardMutationVariables,
) =>
  await graphqlClient.request(
    graphql.CreateOrderFromDigitalCardDocument,
    variables,
  )

export const createCampaign = async (
  variables: CreateCampaignMutationVariables,
) =>
  (await graphqlClient.request(CreateCampaignDocument, variables))
    .createCampaign

export const updateOrder = async (
  variables: graphql.UpdateOrderMutationVariables,
) => await graphqlClient.request(graphql.UpdateOrderDocument, variables)

export const sendOrder = async (
  variables: graphql.SendOrderMutationVariables,
) =>
  await (
    await graphqlClient.request(graphql.SendOrderDocument, variables)
  ).sendOrder

export const updateProductionInfo = async (
  variables: graphql.UpdateProductionInfoMutationVariables,
) =>
  await graphqlClient.request(graphql.UpdateProductionInfoDocument, variables)

export const updateReminder = async (
  variables: graphql.UpdateReminderMutationVariables,
) => {
  const res = await graphqlClient.request(
    graphql.UpdateReminderDocument,
    variables,
  )
  return res
}

export const revertBulkOrder = async (
  variables: RevertBulkOrderMutationVariables,
) => await graphqlClient.request(RevertBulkOrderDocument, variables)

export const createOrUpdateBackPanel = async (
  variables: CreateOrUpdateBackPanelMutationVariables,
) => await graphqlClient.request(CreateOrUpdateBackPanelDocument, variables)

export const deletePanelLayout = async (
  variables: graphql.DeletePanelLayoutMutationVariables,
) => await graphqlClient.request(graphql.DeletePanelLayoutDocument, variables)

export const uploadImage = async (
  variables:
    | graphql.UploadImageMutationVariables
    | { image_file: File; tags: string[] },
) =>
  // @ts-ignore: we're sending "extra" variables as form data
  graphqlClient.request(graphql.UploadImageDocument, variables, {
    [useFormDataHeader]: 'true',
  })

export const createImageWithUrl = async (
  variables: graphql.CreateImageWithUrlMutationVariables,
) => await graphqlClient.request(graphql.CreateImageWithUrlDocument, variables)

export const deleteImage = async (
  variables: graphql.DeleteImageMutationVariables,
) => await graphqlClient.request(graphql.DeleteImageDocument, variables)

export const addFreeCampaign = async (
  variables: AddFreeCampaignMutationVariables,
) => await graphqlClient.request(AddFreeCampaignDocument, variables)

export const changeSubscription = async (
  variables: ChangeSubscriptionMutationVariables,
) => await graphqlClient.request(ChangeSubscriptionDocument, variables)

export const createContact = async (
  variables: graphql.CreateContactMutationVariables,
) => await graphqlClient.request(graphql.CreateContactDocument, variables)

export const createContacts = async (
  variables: graphql.CreateContactsMutationVariables,
) => await graphqlClient.request(graphql.CreateContactsDocument, variables)

export const createContactWithInvite = async (
  variables: graphql.CreateContactWithInviteMutationVariables,
) =>
  await graphqlClient.request(
    graphql.CreateContactWithInviteDocument,
    variables,
  )

export const createContactsWithInvites = async (
  variables: graphql.CreateContactsWithInvitesMutationVariables,
) =>
  await graphqlClient.request(
    graphql.CreateContactsWithInvitesDocument,
    variables,
  )

export const createContactWithSpouseChildren = async (
  variables: graphql.CreateContactWithSpouseChildrenMutationVariables,
) =>
  await graphqlClient.request(
    graphql.CreateContactWithSpouseChildrenDocument,
    variables,
  )

export const updateContact = async (
  variables: graphql.UpdateContactMutationVariables,
) => await graphqlClient.request(graphql.UpdateContactDocument, variables)

export const deleteContact = async (
  variables: graphql.DeleteContactMutationVariables,
) => await graphqlClient.request(graphql.DeleteContactDocument, variables)

export const bulkDeleteContacts = async (
  variables: graphql.BulkDeleteContactsMutationVariables,
) => await graphqlClient.request(graphql.BulkDeleteContactsDocument, variables)

export const separateContact = async (
  variables: graphql.SeparateContactMutationVariables,
) => await graphqlClient.request(graphql.SeparateContactDocument, variables)

export const shareContact = async (
  variables: graphql.ShareContactMutationVariables,
) => await graphqlClient.request(graphql.ShareContactDocument, variables)

export const createContactRequest = async (
  variables: graphql.CreateContactRequestMutationVariables,
) =>
  await graphqlClient.request(graphql.CreateContactRequestDocument, variables)

export const deleteContactRequest = async (
  variables: graphql.DeleteContactRequestMutationVariables,
) =>
  await graphqlClient.request(graphql.DeleteContactRequestDocument, variables)

export const updateRequestedContact = async (
  variables: graphql.UpdateRequestedContactMutationVariables,
) =>
  await graphqlClient.request(graphql.UpdateRequestedContactDocument, variables)

export const createGroup = async (
  variables: graphql.CreateGroupMutationVariables,
) => await graphqlClient.request(graphql.CreateGroupDocument, variables)

export const updateGroup = async (
  variables: graphql.UpdateGroupMutationVariables,
) => await graphqlClient.request(graphql.UpdateGroupDocument, variables)

export const deleteGroup = async (
  variables: graphql.DeleteGroupMutationVariables,
) => await graphqlClient.request(graphql.DeleteGroupDocument, variables)

export const updateInvite = async (
  variables: graphql.UpdateInviteMutationVariables,
) => {
  await graphqlClient.request(graphql.UpdateInviteDocument, variables)
}

export const claimReward = async (
  variables: graphql.ClaimRewardMutationVariables,
) => await graphqlClient.request(graphql.ClaimRewardDocument, variables)

export const createPurchase = async (
  variables: graphql.CreatePurchaseMutationVariables,
) =>
  await (
    await graphqlClient.request(graphql.CreatePurchaseDocument, variables)
  ).createPurchase

export const commitPurchase = async (
  variables: graphql.CommitPurchaseMutationVariables,
) =>
  await (
    await graphqlClient.request(graphql.CommitPurchaseDocument, variables)
  ).commitPurchase

export const updateCoachPublicInfoDisplay = async (
  variables: graphql.UpdateCoachPublicInfoDisplayMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.UpdateCoachPublicInfoDisplayDocument,
      variables,
    )
  ).updateCoachPublicInfoDisplay

export const shouldDisplayUpsaleTrigger = async (
  variables: graphql.ShouldDisplayUpsaleTriggerMutationVariables,
) =>
  (
    await graphqlClient.request(
      graphql.ShouldDisplayUpsaleTriggerDocument,
      variables,
    )
  ).shouldDisplayUpsaleTrigger
